/**
 * Lot Detail Drawer — the unified "click a row" view for Cards + Sealed.
 *
 * Slides in from the right (CSS in styles.css, #drawerRoot in index.html):
 * image + key facts + inline Available/Listed flip + market-price chart
 * (catalog-linked lots) + the lot's own FMV history + an action row that
 * reuses the existing flows. Read-mostly by design: mutating actions (Edit,
 * Refresh FMV, Sell/Trade/Rip) close the drawer first, then hand off to the
 * flow that already owns that mutation. z-index 38 keeps it BELOW the edit
 * modal (40) and the lot-history modal (55) so both stack on top.
 */
import { DB } from "../lib/db";
import { sb } from "../lib/supabase";
import { escapeHtml, toast } from "../lib/dom";
import { fmt$, fmtDate, fmtN } from "../utils/format";
import { icon } from "../utils/icon";
import { statusCellHtml } from "./statusCell";
import { render } from "../state";
import { openEbaySoldsFor } from "../lib/ebaySolds";
import { lotPhotos, lotPhotoUrl, uploadLotPhoto, deleteLotPhotoObject } from "../lib/lotPhotos";
import { openEbayListingModal } from "./ebayListing";
import { endEbayListingManual } from "../lib/ebayDelist";
import { openLabelPrint } from "./labelPrint";
import { openGradedPriceCheck } from "./gradedPriceCheck";

let _open: { kind: "card" | "sealed"; lotId: string } | null = null;

const _escHandler = (e: KeyboardEvent) => { if (e.key === "Escape") closeLotDrawer(); };

function drawerRoot(): HTMLElement {
  let r = document.getElementById("drawerRoot");
  if (!r) { r = document.createElement("div"); r.id = "drawerRoot"; document.body.appendChild(r); }
  return r;
}

function fact(label: string, valueHtml: string): string {
  return `<div><div class="text-xs text-[var(--muted)]">${label}</div><div class="text-sm mt-0.5">${valueHtml}</div></div>`;
}

export function openLotDrawer(kind: "card" | "sealed", lotId: string): void {
  const table = kind === "card" ? "cards" : "sealed";
  const keyField = kind === "card" ? "Card Lot ID" : "Lot ID";
  const r = DB.table(table).find(x => x[keyField] === lotId);
  if (!r) return;
  _open = { kind, lotId };

  const qty        = Number(r["Qty"]) || 0;
  const totalFMV   = Number(r["Current FMV"]) || 0;
  const unitFMV    = qty > 0 ? totalFMV / qty : totalFMV;
  const totalBasis = Number(r["Total Basis"]) || 0;
  const gl         = +(totalFMV - totalBasis).toFixed(2);
  const name = kind === "card" ? String(r["Card Name"] || "") : String(r["Product"] || "");
  const sub  = [
    String(r["Set"] || ""),
    kind === "card" ? (r["Card #"] ? "#" + r["Card #"] : "") : String(r["Product Type"] || ""),
  ].filter(Boolean).join(" \u00b7 ");
  const img    = String(r["Image URL"] || "");
  const pid    = Number(r["TCG Product ID"]) || 0;
  const locked = !!r["FMV Locked"];
  const linked = !!(pid || r["PokemonTCG ID"] || r["Scryfall ID"]);
  const priceSource = locked
    ? `${icon("lock", { size: 12 })} Manual \u2014 locked`
    : linked ? `${icon("link", { size: 12 })} Catalog \u2014 nightly`
    : `${icon("edit", { size: 12 })} Unlinked \u2014 manual`;
  const srcId = String(r["Source ID"] || "");
  const notes = String(r["Notes"] || "");
  const safeLot = escapeHtml(lotId);
  const photos = lotPhotos(r);

  const facts = [
    fact("Qty", `<b class="stat-num">${fmtN(qty)}</b>`),
    kind === "card"
      ? fact("Cond / Grade", escapeHtml(String(r["Condition/Grade"] || "\u2014")))
      : fact("Game", escapeHtml(String(r["Game"] || "\u2014"))),
    fact("Unit FMV", `<b class="stat-num">${locked ? icon("lock", { size: 11 }) + " " : ""}${fmt$(unitFMV)}</b>`),
    fact("Total FMV", `<span class="stat-num">${fmt$(totalFMV)}</span>`),
    fact("Unit Basis", `<span class="stat-num">${fmt$(r["Unit Basis"])}</span>`),
    fact("Total Basis", `<span class="stat-num">${fmt$(totalBasis)}</span>`),
    fact("Unrealized G/L", `<b class="stat-num ${gl >= 0 ? "text-emerald-300" : "text-rose-300"}">${fmt$(gl)}</b>`),
    fact("Price Source", `<span class="text-xs">${priceSource}</span>`),
    fact("Acq Date", escapeHtml(fmtDate(r["Acq Date"]) || "\u2014")),
    fact("Acq Type", escapeHtml(String(r["Acq Type"] || "\u2014"))),
    fact("Source", srcId
      ? `<a class="font-mono text-xs text-cyan-300 hover:underline cursor-pointer" onclick="showLotHistory('${escapeHtml(srcId)}')">${escapeHtml(srcId)}</a>`
      : `<span class="text-[var(--muted)]">\u2014</span>`),
    kind === "card" ? fact("Class", escapeHtml(String(r["Inventory Class"] || "\u2014"))) : fact("Type", escapeHtml(String(r["Product Type"] || "\u2014"))),
  ].join("");

  const actions = `
    <button class="btn btn-primary !py-1 !px-3 text-sm" onclick="drawerEdit()" title="Open the full edit form">${icon("edit")} Edit</button>
    <button class="btn btn-ghost !py-1 !px-3 text-sm" onclick="drawerRefreshFMV()" title="Refresh FMV from market">${icon("refresh")} Refresh FMV</button>
    <button class="btn btn-ghost !py-1 !px-3 text-sm" onclick="drawerLookup()" title="Search this on TCGplayer (new tab)">${icon("search")} TCGplayer</button>
    <button class="btn btn-ghost !py-1 !px-3 text-sm" onclick="drawerEbaySolds()" title="Open eBay SOLD listings, newest first — real recent sale prices (new tab)">${icon("dollar-sign")} eBay Solds</button>
    ${kind === "card" && String(r["Inventory Class"] || "") === "Slab" ? `<button class="btn btn-ghost !py-1 !px-3 text-sm" onclick="drawerGradedCheck()" title="Live eBay asking prices for this slab (lowest first) — one click to apply as FMV">${icon("trending-up")} Graded FMV</button>` : ""}
    <button class="btn btn-ghost !py-1 !px-3 text-sm" onclick="drawerListEbay()" title="Create an eBay listing prefilled from this lot">${icon("upload")} List on eBay</button>
    <button class="btn btn-ghost !py-1 !px-3 text-sm" onclick="drawerGenerate('sale')" title="Start a sale with this lot">${icon("wallet")} Sell</button>
    <button class="btn btn-ghost !py-1 !px-3 text-sm" onclick="drawerGenerate('trade')" title="Start a trade with this lot as outgoing">${icon("route")} Trade</button>
    ${kind === "sealed" ? `<button class="btn btn-ghost !py-1 !px-3 text-sm" onclick="drawerGenerate('rip')" title="Rip / open this product">${icon("scissors")} Rip</button>` : ""}
    <button class="btn btn-ghost !py-1 !px-3 text-sm" onclick="drawerPrintLabel()" title="Print a QR label for this lot (label-writer friendly)">${icon("tag")} Label</button>
    <button class="btn btn-ghost !py-1 !px-3 text-sm" onclick="showLotHistory('${safeLot}')" title="Every purchase, rip, sale, trade, and grading event touching this lot">${icon("layers")} Full History</button>`;

  drawerRoot().innerHTML = `
    <div class="drawer-backdrop" onclick="closeLotDrawer()"></div>
    <div class="drawer-panel">
      <div class="flex items-start gap-3 p-4 border-b border-[var(--border)]">
        ${img ? `<img src="${escapeHtml(img)}" class="rounded border border-[var(--border)] shrink-0" style="height:72px; width:auto; max-width:64px; object-fit:contain" alt="">` : ""}
        <div class="min-w-0 flex-1">
          <div class="font-semibold leading-tight" title="${escapeHtml(name)}">${escapeHtml(name) || "(unnamed lot)"}</div>
          <div class="text-xs text-[var(--muted)] mt-0.5">${escapeHtml(sub)}</div>
          <div class="mt-2 flex items-center gap-2 flex-wrap">
            ${statusCellHtml(table as "cards" | "sealed", lotId, String(r["Status"] || "Available"))}
            <a class="font-mono text-xs text-cyan-300 hover:underline cursor-pointer" onclick="showLotHistory('${safeLot}')">${safeLot}</a>
          </div>
        </div>
        <button class="btn btn-ghost !py-1 !px-2" onclick="closeLotDrawer()" title="Close (Esc)">\u2715</button>
      </div>
      <div class="drawer-body p-4 space-y-4">
        <div class="grid grid-cols-2 gap-x-4 gap-y-3">${facts}</div>
        <div class="flex flex-wrap gap-2 pt-1">${actions}</div>
        <div data-drawer-ebay class="hidden panel-2 p-2 text-xs"></div>
        <div>
          <div class="flex items-center justify-between mb-2">
            <div class="text-xs font-semibold text-[var(--muted)] uppercase tracking-wider">Your photos (${photos.length})</div>
            <button class="btn btn-ghost !py-1 !px-2 text-xs" onclick="drawerAddPhotos()" title="Photos of the actual item (auto-compressed). Provenance now; eBay listings later.">${icon("camera", { size: 12 })} Add</button>
          </div>
          ${photos.length ? `
          <div class="grid grid-cols-4 gap-2">
            ${photos.map((p, i) => `
            <div class="relative group">
              <a href="${escapeHtml(lotPhotoUrl(p))}" target="_blank" rel="noopener" title="Open full size">
                <img src="${escapeHtml(lotPhotoUrl(p))}" class="rounded border border-[var(--border)] w-full object-cover" style="height:72px" loading="lazy" alt="">
              </a>
              <button class="absolute -top-1.5 -right-1.5 hidden group-hover:block bg-rose-600 text-white rounded-full text-xs leading-none" style="width:18px;height:18px" onclick="drawerDeletePhoto(${i})" title="Delete photo">\u2715</button>
            </div>`).join("")}
          </div>` : `<div class="panel-2 p-3 text-xs text-[var(--muted)]">No photos of the actual item yet \u2014 slab shots, corners, backs. They\u2019ll feed eBay listings later.</div>`}
          <input type="file" id="drawerPhotoInput" accept="image/*" multiple class="hidden">
        </div>
        ${pid ? `
        <div>
          <div class="text-xs font-semibold text-[var(--muted)] uppercase tracking-wider mb-2">Market price (90d)</div>
          <div class="panel-2 p-2 relative" style="height:170px">
            <canvas data-drawer-market></canvas>
            <div data-drawer-market-empty class="hidden absolute inset-0 flex items-center justify-center text-xs text-[var(--muted)]">No catalog price history yet.</div>
          </div>
        </div>` : ""}
        <div>
          <div class="text-xs font-semibold text-[var(--muted)] uppercase tracking-wider mb-2">Your FMV history</div>
          <div class="panel-2 p-2 relative" style="height:170px">
            <canvas data-drawer-fmv></canvas>
            <div data-drawer-fmv-empty class="hidden absolute inset-0 flex items-center justify-center text-xs text-[var(--muted)]">Not enough FMV snapshots yet (needs 2+).</div>
          </div>
        </div>
        ${notes ? `
        <div>
          <div class="text-xs font-semibold text-[var(--muted)] uppercase tracking-wider mb-2">Notes</div>
          <div class="panel-2 p-3 text-sm whitespace-pre-wrap">${escapeHtml(notes)}</div>
        </div>` : ""}
      </div>
    </div>`;

  document.addEventListener("keydown", _escHandler);

  // Inline status flip: let updateLotStatus commit, then re-render the drawer
  // so the pill color matches the new value.
  drawerRoot().querySelector<HTMLSelectElement>(".status-select")?.addEventListener("change", () => {
    setTimeout(() => { if (_open && _open.lotId === lotId) openLotDrawer(kind, lotId); }, 60);
  });

  // eBay listing badge — permanent record of "this lot is live on eBay",
  // sourced from ebay_listings (select-own RLS). Shows the latest published
  // listing with a direct link, so the Listed status pill isn't the only clue.
  {
    void (async () => {
      try {
        const { data } = await sb.from("ebay_listings")
          .select("listing_id, environment, price, created_at")
          .eq("lot_id", lotId).eq("status", "published")
          .order("id", { ascending: false }).limit(5);
        const rows = (data || []) as Array<{ listing_id: string | null; environment: string; price: number | null; created_at: string }>;
        const live = rows.filter(r => r.listing_id);
        const el = drawerRoot().querySelector<HTMLElement>("[data-drawer-ebay]");
        if (!el || !live.length) return;
        const top = live[0]!;
        const base = top.environment === "production" ? "https://www.ebay.com/itm/" : "https://sandbox.ebay.com/itm/";
        el.innerHTML = `<span class="text-emerald-300">\u2713 Listed on eBay</span>` +
          ` \u00b7 <a class="text-cyan-300 underline font-mono" href="${escapeHtml(base + top.listing_id)}" target="_blank" rel="noopener" title="Open the eBay listing">#${escapeHtml(String(top.listing_id))}</a>` +
          (top.price != null ? ` \u00b7 ${fmt$(top.price)}` : "") +
          ` \u00b7 ${escapeHtml(new Date(top.created_at).toLocaleDateString())}` +
          (top.environment !== "production" ? ` \u00b7 <span class="text-amber-300">${escapeHtml(top.environment)}</span>` : "") +
          (live.length > 1 ? ` <span class="text-[var(--muted)]">(+${live.length - 1} older)</span>` : "") +
          ` \u00b7 <button class="underline text-rose-300" onclick="drawerEndEbay('${escapeHtml(lotId)}')" title="End the live eBay listing">End listing</button>`;
        el.classList.remove("hidden");
      } catch { /* badge is best-effort */ }
    })();
  }

  // Mount charts lazily — Chart.js stays out of the main bundle (same pattern
  // as lotHistory.ts).
  void (async () => {
    const { renderSealedPriceHistory, renderCatalogPriceHistory, renderLotFmvHistory } = await import("./priceHistoryChart");
    const mc = drawerRoot().querySelector<HTMLCanvasElement>("canvas[data-drawer-market]");
    if (mc && pid) {
      const chart = kind === "sealed"
        ? await renderSealedPriceHistory(mc, pid, 90)
        : await renderCatalogPriceHistory(mc, pid, 90);
      if (!chart) {
        mc.style.display = "none";
        drawerRoot().querySelector<HTMLElement>("[data-drawer-market-empty]")?.classList.remove("hidden");
      }
    }
    const fc = drawerRoot().querySelector<HTMLCanvasElement>("canvas[data-drawer-fmv]");
    if (fc) {
      const chart = await renderLotFmvHistory(fc, kind, lotId);
      if (!chart) {
        fc.style.display = "none";
        drawerRoot().querySelector<HTMLElement>("[data-drawer-fmv-empty]")?.classList.remove("hidden");
      }
    }
  })();
}

export function closeLotDrawer(): void {
  _open = null;
  const r = document.getElementById("drawerRoot");
  if (r) r.innerHTML = "";
  document.removeEventListener("keydown", _escHandler);
}

/* ----- Action handoffs — close the drawer, then reuse the existing flow ----- */
function drawerEdit(): void {
  if (!_open) return;
  const o = _open;
  closeLotDrawer();
  const fn = (window as any)[o.kind === "card" ? "editCard" : "editSealed"];
  if (typeof fn === "function") fn(o.lotId);
}
function drawerRefreshFMV(): void {
  if (!_open) return;
  const o = _open;
  closeLotDrawer();
  const fn = (window as any)[o.kind === "card" ? "refreshCardFMV" : "refreshSealedFMV"];
  if (typeof fn === "function") fn(o.lotId);
}
function drawerLookup(): void {
  if (!_open) return;
  const fn = (window as any)[_open.kind === "card" ? "lookupCardPrice" : "lookupSealedPrice"];
  if (typeof fn === "function") fn(_open.lotId);
}
function drawerEbaySolds(): void {
  if (!_open) return;
  openEbaySoldsFor(_open.kind, _open.lotId);
}
function drawerEndEbay(lotId: string): void {
  void (async () => {
    const ok = await endEbayListingManual(lotId);
    if (!ok) return;
    render(); // refresh the table behind the drawer (status pill Listed -> Available)
    if (_open && _open.lotId === lotId) openLotDrawer(_open.kind, lotId); // refresh badge + pill
  })();
}
function drawerGradedCheck(): void {
  if (!_open || _open.kind !== "card") return;
  const o = _open;
  closeLotDrawer();
  openGradedPriceCheck(o.lotId);
}
function drawerPrintLabel(): void {
  if (!_open) return;
  openLabelPrint(_open.kind, [_open.lotId]);
}
function drawerListEbay(): void {
  if (!_open) return;
  const o = _open;
  closeLotDrawer();
  openEbayListingModal(o.lotId, o.kind);
}
function drawerGenerate(what: "sale" | "trade" | "rip"): void {
  if (!_open) return;
  const o = _open;
  closeLotDrawer();
  if (what === "rip") { (window as any).startRip?.(o.lotId); return; }
  const fn = (window as any)[what === "sale" ? "generateSaleFromLots" : "generateTradeFromLots"];
  if (typeof fn === "function") fn([o.lotId], o.kind);
}

/* ----- Lot photos: add / delete from the drawer ----- */
function drawerAddPhotos(): void {
  const inp = document.getElementById("drawerPhotoInput") as HTMLInputElement | null;
  if (!inp) return;
  inp.onchange = () => { void drawerUploadPhotos(inp); };
  inp.click();
}

async function drawerUploadPhotos(inp: HTMLInputElement): Promise<void> {
  if (!_open) return;
  const o = _open;
  const files = Array.from(inp.files || []);
  inp.value = "";
  if (!files.length) return;
  const table = o.kind === "card" ? "cards" : "sealed";
  const keyField = o.kind === "card" ? "Card Lot ID" : "Lot ID";
  const row = DB.table(table).find(x => x[keyField] === o.lotId);
  if (!row) return;
  toast(`Uploading ${files.length} photo${files.length === 1 ? "" : "s"}\u2026`);
  const paths = [...lotPhotos(row)];
  let ok = 0, failed = 0;
  for (const f of files) {
    try { paths.push(await uploadLotPhoto(f, o.lotId)); ok++; }
    catch (e) { failed++; console.warn("[lotPhotos] upload failed:", e); }
  }
  if (ok) DB.update(table, keyField, o.lotId, { "Photos": paths });
  if (failed) toast(`${ok} added \u00b7 ${failed} failed`, "err");
  else toast(`${ok} photo${ok === 1 ? "" : "s"} added`);
  if (_open && _open.lotId === o.lotId) openLotDrawer(o.kind, o.lotId);
}

function drawerDeletePhoto(idx: number): void {
  if (!_open) return;
  const o = _open;
  const table = o.kind === "card" ? "cards" : "sealed";
  const keyField = o.kind === "card" ? "Card Lot ID" : "Lot ID";
  const row = DB.table(table).find(x => x[keyField] === o.lotId);
  if (!row) return;
  const paths = [...lotPhotos(row)];
  const victim = paths[idx];
  if (!victim) return;
  if (!confirm("Delete this photo? This can\u2019t be undone.")) return;
  paths.splice(idx, 1);
  DB.update(table, keyField, o.lotId, { "Photos": paths });
  void deleteLotPhotoObject(victim);
  openLotDrawer(o.kind, o.lotId);
}

// Inline-handler bridges
(window as any).openLotDrawer    = openLotDrawer;
(window as any).closeLotDrawer   = closeLotDrawer;
(window as any).drawerEdit       = drawerEdit;
(window as any).drawerRefreshFMV = drawerRefreshFMV;
(window as any).drawerLookup     = drawerLookup;
(window as any).drawerEbaySolds  = drawerEbaySolds;
(window as any).drawerListEbay   = drawerListEbay;
(window as any).drawerEndEbay    = drawerEndEbay;
(window as any).drawerPrintLabel = drawerPrintLabel;
(window as any).drawerGradedCheck = drawerGradedCheck;
(window as any).drawerGenerate   = drawerGenerate;
(window as any).drawerAddPhotos    = drawerAddPhotos;
(window as any).drawerDeletePhoto  = drawerDeletePhoto;
